// JOBE-0044.11: NewCollectionModal — controlled-form modal for
// POST /api/collections. Triggered from the CollectionsRailBlock
// header's "+ New collection" action.
//
// Fields: name (required), slug (auto-derived from name until manually
// edited; user-editable), description (optional). Submit posts to
// /api/collections and on 201 closes the modal and calls onCreated()
// so the rail provider can refresh().

import { useCallback, useEffect, useRef, useState } from 'react';

// slugify mirrors the docstore's URN grammar tolerances: lowercase
// ASCII letters / digits / hyphen / underscore / dot. Whitespace runs
// collapse to a single `-`. Anything else is stripped. Leading and
// trailing `-` are trimmed. Server validates definitively; this is
// just user assistance.
export function slugifyName(input) {
  if (!input) return '';
  const lower = String(input).toLowerCase();
  let out = '';
  let lastDash = false;
  for (const ch of lower) {
    if (/[a-z0-9_.]/.test(ch)) {
      out += ch;
      lastDash = false;
    } else if (/\s|-/.test(ch)) {
      if (!lastDash && out.length > 0) {
        out += '-';
        lastDash = true;
      }
    }
  }
  while (out.endsWith('-')) out = out.slice(0, -1);
  return out;
}

export function NewCollectionModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onChangeName = useCallback((e) => {
    const v = e.target.value;
    setName(v);
    if (!slugTouched) setSlug(slugifyName(v));
  }, [slugTouched]);

  const onChangeSlug = useCallback((e) => {
    setSlug(e.target.value);
    setSlugTouched(true);
  }, []);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    const nameTrim = name.trim();
    const slugTrim = slug.trim();
    if (!nameTrim || !slugTrim) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        slug: slugTrim,
        name: nameTrim,
      };
      const descTrim = description.trim();
      if (descTrim) body.description = descTrim;
      const r = await fetch('/api/collections', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.status === 201) {
        let row = null;
        try { row = await r.json(); } catch { /* tolerate empty */ }
        setSubmitting(false);
        if (onCreated) onCreated(row);
        if (onClose) onClose();
        return;
      }
      let payload = null;
      try { payload = await r.json(); } catch { /* tolerate */ }
      setError({
        status: r.status,
        code: payload?.error || null,
        message: payload?.message || null,
      });
      setSubmitting(false);
    } catch (err) {
      setError({ status: 0, code: null, message: err?.message || String(err) });
      setSubmitting(false);
    }
  }, [name, slug, description, onClose, onCreated]);

  const canSubmit = name.trim().length > 0 && slug.trim().length > 0 && !submitting;

  return (
    <div
      className="scrim new-collection-scrim"
      data-testid="new-collection-scrim"
      onClick={() => !submitting && onClose?.()}
    >
      <form
        className="modal new-collection-modal"
        role="dialog"
        aria-modal="true"
        aria-label="New collection"
        data-testid="new-collection-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <header className="new-collection-head">
          <h3 className="lane-title">New collection</h3>
          <button
            type="button"
            className="new-collection-close"
            onClick={() => onClose?.()}
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </header>
        <div className="new-collection-body">
          <label className="new-collection-field">
            <span className="new-collection-field-label">Name</span>
            <input
              ref={nameRef}
              type="text"
              className="new-collection-input"
              value={name}
              onChange={onChangeName}
              maxLength={200}
              data-testid="new-collection-name"
              autoComplete="off"
            />
          </label>
          <label className="new-collection-field">
            <span className="new-collection-field-label">Slug</span>
            <input
              type="text"
              className="new-collection-input mono"
              value={slug}
              onChange={onChangeSlug}
              maxLength={128}
              data-testid="new-collection-slug"
              autoComplete="off"
            />
          </label>
          <label className="new-collection-field">
            <span className="new-collection-field-label">Description</span>
            <textarea
              className="new-collection-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="new-collection-description"
            />
          </label>
          {error ? (
            <p className="new-collection-error" data-testid="new-collection-error">
              {error.code === 'slug_in_use'
                ? `Slug "${slug}" is already in use.`
                : error.message
                  ? error.message
                  : `couldn't create (${error.status})`}
            </p>
          ) : null}
        </div>
        <footer className="new-collection-foot">
          <button
            type="button"
            className="new-collection-cancel"
            onClick={() => onClose?.()}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="new-collection-commit"
            disabled={!canSubmit}
            data-testid="new-collection-commit"
          >
            {submitting ? 'Creating…' : 'Create'}
          </button>
        </footer>
      </form>
    </div>
  );
}
